import type { Equipamento, MotivoExclusaoAtivo, PropriedadeAtivo } from '../../../../models';
import { normalizarPatrimonio } from '../../../../models';
import { textoCorresponde } from '../../../../utils/buscaTexto';
import {
  DETALHES_SITUACAO_ATIVO,
  PREFIXOS_PATRIMONIO,
  SITUACAO_ATIVO_LABELS,
  SITUACAO_POR_STATUS,
  TIPOS_COM_NSERIE_OBRIGATORIO,
  TIPOS_COM_PREFIXO_PADRAO,
  TIPO_ATIVO_LABELS,
  rotuloPatrimonio,
  type SituacaoAtivo,
} from './ativos.definitions';

export interface ContextoAtivos {
  nomeClientePorId: Map<string, string>;
  nomeColaboradorPorId: Map<string, string>;
}

export function situacaoDoAtivo(equipamento: Equipamento): SituacaoAtivo {
  return SITUACAO_POR_STATUS[equipamento.statusCustodia] ?? 'em_estoque';
}

/**
 * Complemento da Situação pra exibir ("Casa Raro", "Reservada", "para o BMG"...). O detalhe
 * escolhido manualmente só vale se ainda pertencer à Situação atual (as automações podem
 * ter mudado a Situação depois); os demais são derivados de onde o ativo está.
 */
export function detalheSituacaoDoAtivo(equipamento: Equipamento, contexto: ContextoAtivos): string | null {
  const situacao = situacaoDoAtivo(equipamento);
  const escolhido = equipamento.situacaoDetalhe
    ? DETALHES_SITUACAO_ATIVO[situacao]?.[equipamento.situacaoDetalhe]
    : undefined;
  if (escolhido) return escolhido;

  const cliente = equipamento.clienteAtualId ? contexto.nomeClientePorId.get(equipamento.clienteAtualId) : null;
  switch (situacao) {
    case 'em_estoque':
      return 'Casa Raro';
    case 'em_preparo':
    case 'aguardando_devolucao':
    case 'em_transito': {
      // Movimentação em andamento: quem / pra onde (ver Equipamento.transito).
      const t = equipamento.transito;
      const pessoa = t?.colaboradorId ? contexto.nomeColaboradorPorId.get(t.colaboradorId) ?? 'o colaborador' : null;
      if (t && situacao === 'em_preparo') return pessoa ? `para ${pessoa}` : null;
      if (t && situacao === 'aguardando_devolucao') return pessoa ? `com ${pessoa}` : null;
      if (t?.sentido === 'envio') return pessoa ? `indo para ${pessoa}` : 'indo para o colaborador';
      if (t?.sentido === 'devolucao') {
        const destino = t.destinoClienteId ? contexto.nomeClientePorId.get(t.destinoClienteId) ?? 'o Cliente' : 'a Raro';
        return pessoa ? `voltando de ${pessoa} para ${destino}` : `voltando para ${destino}`;
      }
      // Envio pra Cliente (Estadia) — sem Movimentação.
      return situacao === 'em_transito' && cliente ? `para ${cliente}` : null;
    }
    case 'em_manutencao':
      return equipamento.fornecedorManutencao ?? null;
    case 'no_cliente':
      return [cliente, equipamento.statusCustodia === 'com_cliente' ? 'Pronta' : null].filter(Boolean).join(' · ') || null;
    case 'devolvido_cliente': {
      const proprietario = equipamento.clienteProprietarioId
        ? contexto.nomeClientePorId.get(equipamento.clienteProprietarioId)
        : null;
      return cliente ?? proprietario ?? null;
    }
    default:
      return null;
  }
}

export function prefixoDoPatrimonio(patrimonio: string | null | undefined) {
  const normalizado = normalizarPatrimonio(patrimonio);
  if (!normalizado) return null;
  return PREFIXOS_PATRIMONIO.find((p) => normalizado.startsWith(p.prefixo)) ?? null;
}

/** Alerta (não bloqueia) quando o prefixo indica uma propriedade diferente da escolhida. */
export function alertaPrefixoPropriedade(
  patrimonio: string | null | undefined,
  tipo: string | null | undefined,
  propriedade: PropriedadeAtivo | null | undefined
): string | null {
  if (!tipo || !TIPOS_COM_PREFIXO_PADRAO.includes(tipo)) return null;
  const prefixo = prefixoDoPatrimonio(patrimonio);
  if (!prefixo) {
    return normalizarPatrimonio(patrimonio)
      ? 'Prefixo fora do padrão (RLMP, RLDL, RLLN, RLSG, BP, ALG).'
      : null;
  }
  if (propriedade && propriedade !== prefixo.propriedade) {
    return `O prefixo ${prefixo.prefixo} indica ${prefixo.descricao}, mas a Propriedade está como ${
      propriedade === 'raro' ? 'Raro' : 'Cliente'
    }.`;
  }
  return null;
}

export type TipoPendenciaAtivo =
  | 'patrimonio_ausente'
  | 'patrimonio_fora_padrao'
  | 'prefixo'
  | 'propriedade_ausente'
  | 'cliente_proprietario_ausente'
  | 'nserie_ausente'
  | 'duplicado'
  | 'em_uso_sem_projeto'
  | 'historico_nao_buscado';

export const MOTIVO_EXCLUSAO_ATIVO_LABELS: Record<MotivoExclusaoAtivo, string> = {
  cadastro_engano: 'Cadastrado por engano',
  duplicado: 'Duplicado',
  outro: 'Outro',
};

/** Situações de quem está em uso/processo (vêm das Movimentações/Estadias) — não exclui. */
const SITUACOES_QUE_BLOQUEIAM_EXCLUSAO: SituacaoAtivo[] = ['em_uso', 'em_transito', 'em_preparo', 'aguardando_devolucao', 'no_cliente'];

/** null = pode excluir; senão, o que resolver antes (decisão: ativo com alguém ou em processo
 * não some — registra a devolução/conclui a Movimentação primeiro). */
export function bloqueioExclusaoAtivo(ativo: Equipamento, contexto: ContextoAtivos): string | null {
  if (ativo.colaboradorAtualId) {
    const nome = contexto.nomeColaboradorPorId.get(ativo.colaboradorAtualId) ?? 'um colaborador';
    return `Está com ${nome}. Registre a devolução antes de excluir.`;
  }
  const situacao = situacaoDoAtivo(ativo);
  if (SITUACOES_QUE_BLOQUEIAM_EXCLUSAO.includes(situacao)) {
    return `Está "${SITUACAO_ATIVO_LABELS[situacao]}" — conclua a Movimentação/Estadia (ou ajuste a Situação) antes de excluir.`;
  }
  return null;
}

/** Rótulo curto de cada tipo de pendência — usado no filtro da lista. */
export const ROTULO_TIPO_PENDENCIA: Record<TipoPendenciaAtivo, string> = {
  patrimonio_ausente: 'Sem patrimônio / identificador',
  patrimonio_fora_padrao: 'Patrimônio fora do padrão',
  prefixo: 'Prefixo diferente da propriedade',
  propriedade_ausente: 'Propriedade não informada',
  cliente_proprietario_ausente: 'Propriedade de Cliente sem dizer qual',
  nserie_ausente: 'Sem nº de série',
  duplicado: 'Possível duplicado',
  em_uso_sem_projeto: 'Em uso sem Projeto',
  historico_nao_buscado: 'Histórico anterior não buscado',
};

export interface PendenciaAtivo {
  tipo: TipoPendenciaAtivo;
  mensagem: string;
}

/** Índice patrimônio normalizado / nº série → ids — pra achar duplicados sem O(n²). */
export function indexarDuplicados(ativos: Equipamento[]) {
  const porPatrimonio = new Map<string, string[]>();
  const porSerie = new Map<string, string[]>();
  for (const ativo of ativos) {
    const patrimonio = normalizarPatrimonio(ativo.patrimonio);
    if (patrimonio) porPatrimonio.set(patrimonio, [...(porPatrimonio.get(patrimonio) ?? []), ativo.id]);
    const serie = ativo.nSerie?.trim().toUpperCase();
    if (serie) porSerie.set(serie, [...(porSerie.get(serie) ?? []), ativo.id]);
  }
  return { porPatrimonio, porSerie };
}

/**
 * O que falta sanar no cadastro (discovery §7.1) — em vez de script de correção, o sistema
 * aponta e o SI resolve na tela. Pensado sobretudo pros equipamentos criados pelas
 * Movimentações antes da ficha existir.
 */
export function pendenciasDoAtivo(
  ativo: Equipamento,
  indice: ReturnType<typeof indexarDuplicados>
): PendenciaAtivo[] {
  const pendencias: PendenciaAtivo[] = [];
  const rotulo = rotuloPatrimonio(ativo.tipo);
  const normalizado = normalizarPatrimonio(ativo.patrimonio);

  if (!normalizado) {
    pendencias.push({ tipo: 'patrimonio_ausente', mensagem: `Sem ${rotulo.toLowerCase()}.` });
  } else if (ativo.patrimonio !== normalizado) {
    pendencias.push({
      tipo: 'patrimonio_fora_padrao',
      mensagem: `${rotulo} fora do padrão ("${ativo.patrimonio}" → "${normalizado}").`,
    });
  }

  const alertaPrefixo = alertaPrefixoPropriedade(ativo.patrimonio, ativo.tipo, ativo.propriedade);
  if (alertaPrefixo) pendencias.push({ tipo: 'prefixo', mensagem: alertaPrefixo });

  if (!ativo.propriedade) {
    pendencias.push({ tipo: 'propriedade_ausente', mensagem: 'Propriedade não informada (Raro ou Cliente).' });
  } else if (ativo.propriedade === 'cliente' && !ativo.clienteProprietarioId) {
    pendencias.push({ tipo: 'cliente_proprietario_ausente', mensagem: 'Propriedade de Cliente, mas sem dizer qual.' });
  }

  if (TIPOS_COM_NSERIE_OBRIGATORIO.includes(ativo.tipo) && !ativo.nSerie?.trim()) {
    pendencias.push({
      tipo: 'nserie_ausente',
      mensagem: `Sem nº de série (obrigatório pra ${TIPO_ATIVO_LABELS[ativo.tipo] ?? ativo.tipo}).`,
    });
  }

  const mesmos = new Set<string>();
  if (normalizado) indice.porPatrimonio.get(normalizado)?.forEach((id) => mesmos.add(id));
  const serie = ativo.nSerie?.trim().toUpperCase();
  if (serie) indice.porSerie.get(serie)?.forEach((id) => mesmos.add(id));
  mesmos.delete(ativo.id);
  if (mesmos.size > 0) {
    pendencias.push({
      tipo: 'duplicado',
      mensagem: `Possível duplicado: ${mesmos.size === 1 ? 'outro cadastro' : `${mesmos.size} outros cadastros`} com o mesmo ${rotulo.toLowerCase()} ou nº de série.`,
    });
  }

  // O Projeto vem sozinho das Movimentações só daqui pra frente — o que já estava com alguém
  // antes (e o GLPI não informava) fica sem; aponta pra completar na ficha.
  if (situacaoDoAtivo(ativo) === 'em_uso' && !ativo.projetoClienteId) {
    pendencias.push({ tipo: 'em_uso_sem_projeto', mensagem: 'Em uso sem Projeto — informe o projeto (Cliente) em que é usado.' });
  }

  // Cadastro anterior ao histórico: o passado só entra quando o SI buscar (aba Histórico).
  if (!ativo.historicoDesdeCriacao && !ativo.historicoRecuperadoEm) {
    pendencias.push({
      tipo: 'historico_nao_buscado',
      mensagem: 'Histórico anterior ainda não buscado (aba Histórico → "Buscar histórico nas Solicitações").',
    });
  }

  return pendencias;
}

/** Busca livre (patrimônio, série, modelo, fabricante, linha, cartão, colaborador), ignorando
 * acento, espaço e pontuação — ver textoCorresponde. */
export function ativoCorrespondeTexto(ativo: Equipamento, termo: string, contexto: ContextoAtivos): boolean {
  return textoCorresponde(
    [
      ativo.patrimonio,
      ativo.nSerie,
      ativo.modelo,
      ativo.fabricante,
      ativo.linha,
      ativo.numeroCartao,
      ativo.colaboradorAtualId ? contexto.nomeColaboradorPorId.get(ativo.colaboradorAtualId) : null,
    ],
    termo
  );
}

/** Filtro do painel — mesma convenção dos outros painéis: lista vazia = sem filtro. */
export interface SelecaoAtivos {
  situacoes: SituacaoAtivo[];
  tipos: string[];
  /** 'raro' ou o id do Cliente proprietário (ver chavePropriedade). */
  propriedades: string[];
  projetos: string[];
}

export type FacetaAtivos = keyof SelecaoAtivos;

export const SEM_PROJETO_ATIVO = '__sem_projeto__';

/** Propriedade como aparece no painel: 'raro' ou o id do Cliente dono (ativo de Cliente
 * sem dizer qual fica sem valor — é pendência). */
export function chavePropriedade(ativo: Equipamento): string | null {
  if (ativo.propriedade === 'raro') return 'raro';
  if (ativo.propriedade === 'cliente') return ativo.clienteProprietarioId ?? null;
  return null;
}

function valoresDo(ativo: Equipamento): Record<FacetaAtivos, string | null> {
  return {
    situacoes: situacaoDoAtivo(ativo),
    tipos: ativo.tipo,
    propriedades: chavePropriedade(ativo),
    projetos: ativo.projetoClienteId || SEM_PROJETO_ATIVO,
  };
}

export function aplicarSelecaoAtivos(lista: Equipamento[], selecao: SelecaoAtivos, ignorar?: FacetaAtivos): Equipamento[] {
  return lista.filter((ativo) => {
    const valores = valoresDo(ativo);
    return (Object.keys(selecao) as FacetaAtivos[]).every((faceta) => {
      if (faceta === ignorar) return true;
      const selecionados = selecao[faceta] as string[];
      return selecionados.length === 0 || (valores[faceta] != null && selecionados.includes(valores[faceta] as string));
    });
  });
}

/** Cada critério conta com os outros filtros aplicados, menos ele mesmo (mesma regra dos
 * outros painéis de resumo). */
export function contarAtivos(base: Equipamento[], selecao: SelecaoAtivos): Record<FacetaAtivos, Map<string, number>> {
  const resultado = {} as Record<FacetaAtivos, Map<string, number>>;
  for (const faceta of Object.keys(selecao) as FacetaAtivos[]) {
    const contagem = new Map<string, number>();
    for (const ativo of aplicarSelecaoAtivos(base, selecao, faceta)) {
      const valor = valoresDo(ativo)[faceta];
      if (valor != null) contagem.set(valor, (contagem.get(valor) ?? 0) + 1);
    }
    resultado[faceta] = contagem;
  }
  return resultado;
}
